import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import {
  HdsTabs,
  HdsLinkInline,
  HdsFormTextInputField,
} from '@hashicorp/design-system-components/components';
import { Typography, FlexGrid, DisclosureGuide } from 'core/components';
import { boxPadding } from 'core/modifiers';
import {
  CONTENT_TABS_LABELS,
  PRODUCT_CONSUL,
} from 'networks-common/utils/content-tabs';
import { AZURE_DISCLOSURE_GUIDES_PEERING } from 'networks-common/utils/azure-connections';
import {
  isActivePeering,
  isPendingAcceptance,
} from 'networks-common/utils/peerings';
import Terminal from 'hcp/components/page/hvn-peerings/detail/content-tabs/azure/updating-security-groups/terminal';
import WebConsole from 'hcp/components/page/hvn-peerings/detail/content-tabs/azure/updating-security-groups/web-console';

export default class PeeringsDetailAzureUpdatingSecurityGroups extends Component {
  @service router;

  @tracked securityGroupId = '';
  @tracked routeTableId;

  tabs = CONTENT_TABS_LABELS;
  products = {
    consul: PRODUCT_CONSUL,
  };
  azureDisclosureGuides = AZURE_DISCLOSURE_GUIDES_PEERING;

  updateQueryParams = (queryParams = {}) => {
    return this.router.transitionTo(this.router.currentRouteName, {
      queryParams,
    });
  };

  updateSecurityGroupId = ({ target }) => {
    this.securityGroupId = target.value;
  };

  get isActive() {
    return isActivePeering(this.args.peering);
  }

  get isPending() {
    return isPendingAcceptance(this.args.peering);
  }
  <template>
    <div ...attributes>
      <HdsTabs as |T|>
        <T.Tab
          @isSelected={{eq @tab this.tabs.terminal.id}}
          data-test-peering-terminal-tab-azure
          {{on
            "click"
            (fn this.updateQueryParams (hash tab=this.tabs.terminal.id))
          }}
        >
          {{t this.tabs.cloudShell.label}}
        </T.Tab>
        <T.Panel />

        <T.Tab
          @isSelected={{eq @tab this.tabs.webConsole.id}}
          data-test-peering-web-console-tab-azure-consul
          {{on
            "click"
            (fn this.updateQueryParams (hash tab=this.tabs.webConsole.id))
          }}
        >
          {{t this.tabs.webConsole.label}}
        </T.Tab>
        <T.Panel />
      </HdsTabs>

      {{! Security Groups title and instructions}}
      <div {{boxPadding "xl 0 0 0"}}>
        <Typography
          @variant="h3"
          class="hds-typography-display-300 hds-font-weight-semibold hds-foreground-primary"
          {{boxPadding "0 0 sm 0"}}
        >
          {{t
            "components.page.hvn-peerings.detail.instructions.azure.active.terminal.updating-security-groups.title"
          }}
          {{#if (eq @tab this.tabs.webConsole.id)}}
            <HdsLinkInline
              class="headerLink"
              @href="https://docs.microsoft.com/en-us/azure/virtual-network/manage-network-security-group"
              @icon="external-link"
              @iconPosition="trailing"
              @isHrefExternal={{true}}
            >
              {{t
                "components.page.hvn-peerings.detail.instructions.azure.active.web-console.updating-security-groups.link"
              }}
            </HdsLinkInline>
          {{/if}}
        </Typography>
        <div {{boxPadding "0 0 md 0"}}>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="12" @lg="8">
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 md 0"}}
              >
                {{#if (eq @tab this.tabs.terminal.id)}}
                  {{t
                    "components.page.hvn-peerings.detail.instructions.azure.active.terminal.updating-security-groups.message"
                    hvnID=@model.network.id
                    htmlSafe=true
                  }}
                {{else if (eq @tab this.tabs.webConsole.id)}}
                  {{t
                    "components.page.hvn-peerings.detail.instructions.azure.active.web-console.updating-security-groups.message"
                    hvnID=@model.network.id
                    htmlSafe=true
                  }}
                {{/if}}
              </Typography>
              {{#if (eq @tab this.tabs.webConsole.id)}}
                <DisclosureGuide
                  @content={{this.azureDisclosureGuides.updatingSecurityGroups}}
                />
              {{/if}}
            </G.Item>
          </FlexGrid>
        </div>
      </div>
      {{#if (eq @tab this.tabs.terminal.id)}}
        {{! Terminal Content }}
        <form
          data-test-peering-terminal-updating-security-groups-form-azure
          class="hcp-hds-form"
          aria-labelledby={{t
            "components.page.hvn-peerings.detail.instructions.default.updating-groups.terminal.aria-label"
          }}
        >
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="12" @lg="4">
              <div {{boxPadding "0 0 sm 0"}}>
                <HdsFormTextInputField
                  @id="securityGroupId"
                  @type="text"
                  @value={{this.securityGroupId}}
                  name="securityGroupId"
                  data-test-security-group-id-azure
                  {{on "change" this.updateSecurityGroupId}}
                  as |F|
                >
                  <F.Label>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.azure.active.terminal.updating-security-groups.label"
                    }}
                  </F.Label>
                  <F.HelperText>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.azure.active.terminal.updating-security-groups.help-text"
                    }}
                  </F.HelperText>
                </HdsFormTextInputField>
              </div>
            </G.Item>
          </FlexGrid>

          <HdsTabs as |T|>
            <T.Tab
              @icon="consul-color"
              @isSelected={{eq @product this.products.consul.id}}
              data-test-peering-terminal-consul-tab-azure
              {{on
                "click"
                (fn
                  this.updateQueryParams (hash product=this.products.consul.id)
                )
              }}
            >
              {{t "components.networks.content-tabs.consul-snippets.label"}}
            </T.Tab>
            <T.Panel />
          </HdsTabs>

          <Terminal
            @model={{@model}}
            @securityGroupId={{this.securityGroupId}}
            @product={{@product}}
            @products={{this.products}}
          />
        </form>
      {{else if (eq @tab this.tabs.webConsole.id)}}
        <HdsTabs as |T|>
          <T.Tab
            @icon="consul-color"
            @isSelected={{eq @product this.products.consul.id}}
            data-test-peering-web-console-consul-tab-azure
            {{on
              "click"
              (fn this.updateQueryParams (hash product=this.products.consul.id))
            }}
          >
            {{t "components.networks.content-tabs.consul-rules.label"}}
          </T.Tab>
          <T.Panel />
        </HdsTabs>

        <WebConsole
          @model={{@model}}
          @securityGroupId={{this.securityGroupId}}
          @product={{@product}}
          @products={{this.products}}
        />
      {{/if}}
    </div>
  </template>
}
